/** Client for the review API (task 4120.2). */
import { apiFetch } from "../../app/api";

const BASE = "/api/v1/review";

export interface Signal {
  name: string;
  weight: number;
  detail: string;
}

export interface QueueRow {
  case_id: string;
  claim_id: string;
  entry_reason: string;
  opened_at: string;
  score: number;
  signals: Signal[];
}

export interface QueueMetrics {
  open_cases: number;
  oldest_age_seconds: number | null;
  median_age_seconds: number | null;
  closed_last_day: number;
}

export interface Anchor {
  source_region_id: string;
  region_key: string;
  region_kind: string;
  parsed_document_id: string;
  source_artifact_version_id: string;
  page_number: number | null;
  sheet_name: string | null;
  cell_ref: string | null;
}

export interface Alternative {
  id: string;
  value: unknown;
  materiality: string;
  standing: string;
  rationale: string;
}

export interface CaseView {
  case_id: string;
  case_state: string;
  entry_reason: string;
  claim: {
    id: string;
    statement: string;
    lane: string;
    status: string;
    target_object: string | null;
    target_attribute: string | null;
    boundary: Record<string, unknown> | null;
    anchors: Anchor[];
  };
  proposal: {
    id: string;
    result_form: string;
    output_kind: string;
    proposed_value: unknown;
    reasoning: string;
    alternatives: Alternative[];
  } | null;
  evidence: {
    item_id: string;
    stance: string;
    standing: string;
    description: string;
    channel_key: string;
    source_artifact_version_id: string | null;
    authority_role: string | null;
    proof_limit: string | null;
    applicable: boolean | null;
  }[];
  tests: {
    result_id: string;
    test_key: string;
    version: number;
    outcome: string;
    reason: string;
    profile_label: string | null;
  }[];
  grade: {
    grade: string;
    parameter_set_key: string;
    parameter_set_version: number;
    inputs: Record<string, unknown>;
  } | null;
  signals: Signal[];
  precedents: {
    case_id: string;
    claim_id: string;
    related_by: string;
    ruling_kind: string;
    rationale: string;
  }[];
  impact: {
    claims_with_same_target: number;
    claims_with_same_mapping: number;
    active_rules_on_mapping: number;
  };
  rulings: { id: string; ruling_kind: string; rationale: string; ruled_at: string }[];
}

export function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

export function listQueue(engagementId: string): Promise<QueueRow[]> {
  return apiFetch<QueueRow[]>(`${BASE}/queue?engagement_id=${encodeURIComponent(engagementId)}`);
}

export function getMetrics(engagementId: string): Promise<QueueMetrics> {
  return apiFetch<QueueMetrics>(`${BASE}/metrics?engagement_id=${encodeURIComponent(engagementId)}`);
}

export function getCase(caseId: string): Promise<CaseView> {
  return apiFetch<CaseView>(`${BASE}/cases/${encodeURIComponent(caseId)}`);
}

function post(caseId: string, path: string, body: unknown): Promise<unknown> {
  return apiFetch(`${BASE}/cases/${encodeURIComponent(caseId)}/${path}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function ruleOnEvidence(
  caseId: string,
  itemId: string,
  action: "admit" | "exclude",
  rationale: string,
): Promise<unknown> {
  return post(caseId, "evidence", { item_id: itemId, action, rationale });
}

export function decideProposal(
  caseId: string,
  proposalId: string,
  action: "accept" | "reject",
  rationale: string,
): Promise<unknown> {
  return post(caseId, "proposal", { proposal_id: proposalId, action, rationale });
}

export function chooseAlternative(
  caseId: string,
  proposalId: string,
  alternativeId: string,
  rationale: string,
): Promise<unknown> {
  return post(caseId, "alternative", {
    proposal_id: proposalId,
    alternative_id: alternativeId,
    rationale,
  });
}

export function markNoSupport(caseId: string, explanation: string): Promise<unknown> {
  return post(caseId, "no-support", {
    reasons: { codes: ["insufficient_evidence"], explanation },
  });
}

export function anchorText(anchor: Anchor): string {
  const parts = [
    anchor.sheet_name && `sheet ${anchor.sheet_name}`,
    anchor.cell_ref && `cell ${anchor.cell_ref}`,
    anchor.page_number !== null && `page ${anchor.page_number}`,
    `${anchor.region_kind} ${anchor.region_key}`,
  ];
  return parts.filter(Boolean).join(", ");
}
